import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Chip, TextField, Typography } from '@mui/material';
import { addDays, format, isValid, parseISO, startOfDay, subDays } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from 'api/API';
import { UssHistoryDevice, UssHistoryEvent, UssHistoryResponse } from 'api/Responses/SetSerialNumberResponses';
import Theme from 'App.theme';
import Button from 'components/Button';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import * as Styles from 'pages/ManageSerialNumbers/ManageSerialNumbers.styles';
import { FileService } from 'services/FileService';
import { formatDateTimeWithZone } from 'utils/helpers/DateTimeFormat.helpers';

type SerialNumberHistoryParams = {
  code: string;
  setUssId: string;
};

const dotColour = (event: UssHistoryEvent): 'grey' | 'primary' | 'error' | 'success' => {
  if (event.event_type === 'INSPECTION') {
    if (event.devices.length > 0) {
      return 'error';
    }
    // UNCONFIRMED rows are placeholders created with the delivery, not inspections yet
    return event.status === 'CONFIRMED' ? 'primary' : 'grey';
  }
  return event.event_type === 'ORDER_UPLOAD' ? 'grey' : 'success';
};

const eventKey = (event: UssHistoryEvent): string =>
  [event.event_type, event.occurred_at, event.stage, event.order_number, event.delivery_number].join('|');

const deviceKey = (device: UssHistoryDevice): string =>
  [device.part_id, device.description, device.expected_qty, device.confirmed_qty].join('|');

const SerialNumberHistory: FC = () => {
  const [language] = useLanguage();
  const text = language.SerialNumberHistory;
  const { code = '', setUssId = '' } = useParams<SerialNumberHistoryParams>();
  const navigate = useNavigate();
  const [response, setResponse] = useState<UssHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // yyyy-MM-dd strings, as held by <input type="date">
  const [dateFrom, setDateFrom] = useState(() => format(subDays(new Date(), 21), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const events = response?.events ?? [];
  // Inputs always hold what the user typed (resetting a controlled date input wipes the browser's typing buffer).
  // A request is only made for a complete, sane range: valid dates, years >= 2000 (so half-typed years like
  // 0202 don't fetch the full history) and From <= To. Otherwise the last valid range is kept, so the current
  // results, or a request already in flight, stay as they are.
  const from = parseISO(dateFrom);
  const to = parseISO(dateTo);
  const range = useRef({ occurredFrom: '', occurredTo: '' });
  const rangeValid = isValid(from) && isValid(to) && from.getFullYear() >= 2000 && from <= to;
  if (rangeValid) {
    // Half-open range: occurred_to is exclusive, so send the start of the day after "To".
    // parseISO of a date-only string is local midnight, or the first hour after it when a
    // DST change skips midnight; startOfDay stops that hour carrying over to the next day.
    range.current = { occurredFrom: from.toISOString(), occurredTo: startOfDay(addDays(to, 1)).toISOString() };
  }
  const { occurredFrom, occurredTo } = range.current;

  useEffect(() => {
    // Ignore completions from a previous serial or range when either changes mid-request
    let stale = false;
    setResponse(null);
    setLoading(true);
    setLoadFailed(false);
    API.UssEndpoint.getSerialNumberHistory(setUssId, { occurredFrom, occurredTo })
      .then(data => {
        if (!stale) {
          setResponse(data);
        }
      })
      .catch(() => {
        if (!stale) {
          toast.error(text.loadError);
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!stale) {
          setLoading(false);
        }
      });
    return () => {
      stale = true;
    };
    // text is static for the page's lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUssId, occurredFrom, occurredTo]);

  const openFile = async (path: string) => {
    // Open the tab synchronously inside the click so popup blockers allow it, then point it at the file.
    // `noopener` as a feature would make window.open return null, so sever the opener by hand instead.
    const fileTab = window.open('', '_blank');
    if (fileTab) {
      fileTab.opener = null;
    }
    try {
      const url = await FileService.getFile(path, true, true);
      if (fileTab) {
        fileTab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch {
      fileTab?.close();
      toast.error(text.fileUnavailable);
    }
  };

  const orderDetails = (event: UssHistoryEvent): string =>
    [
      event.order_number && `${text.Labels.orderNumber} ${event.order_number}`,
      event.delivery_number && `${text.Labels.deliveryNumber} ${event.delivery_number}`,
      event.hospital,
      // surgery_date is a hospital-local calendar date (yyyy-MM-dd); parse it as local, not UTC
      event.surgery_date && `${text.Labels.surgeryDate} ${format(parseISO(event.surgery_date), 'dd/MM/yyyy')}`,
      event.surgeon_name && `${text.Labels.surgeon} ${event.surgeon_name}`,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <Page>
      <Styles.SetHeader>
        <div>
          <h1>{response?.uss ?? ''}</h1>
          <span>{response?.display_code ?? code}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button onClick={() => navigate(-1)}>{text.back}</Button>
        </div>
      </Styles.SetHeader>
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <TextField
          type="date"
          size="small"
          label={text.Filters.from}
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: dateTo }}
          error={!rangeValid}
        />
        <TextField
          type="date"
          size="small"
          label={text.Filters.to}
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: dateFrom }}
          error={!rangeValid}
        />
      </div>
      {loading && <PageSpinner />}
      {!loading && events.length === 0 && (
        <div style={{ backgroundColor: Theme.greys.GREY0, padding: '4rem', marginTop: '2rem', textAlign: 'center' }}>
          {loadFailed ? text.loadError : text.noEvents}
        </div>
      )}
      {!loading && events.length > 0 && (
        <Timeline position="right" sx={{ marginTop: '2rem', padding: 0 }}>
          {events.map((event, index) => {
            const workflow = event.workflow ?? event.stage;
            return (
              <TimelineItem key={`${eventKey(event)}|${index}`}>
                <TimelineOppositeContent sx={{ flex: 0.2, color: Theme.greys.GREY3 }}>
                  {formatDateTimeWithZone(event.occurred_at)}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={dotColour(event)} />
                  {index < events.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ paddingBottom: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold">{text.events[event.event_type]}</Typography>
                    {workflow && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={(text.workflows as Record<string, string>)[workflow] ?? workflow}
                      />
                    )}
                    {event.status && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={event.status === 'CONFIRMED' ? 'success' : 'default'}
                        label={event.status}
                      />
                    )}
                  </div>
                  {event.performed_by && (
                    <Typography variant="body2" color={Theme.greys.GREY3}>
                      {`${text.Labels.by} ${event.performed_by}`}
                    </Typography>
                  )}
                  {orderDetails(event) && <Typography variant="body2">{orderDetails(event)}</Typography>}
                  {event.devices.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {text.Labels.devices}
                      </Typography>
                      <ul style={{ margin: '0.25rem 0 0 0', paddingLeft: '1.25rem' }}>
                        {event.devices.map(device => (
                          <li key={deviceKey(device)}>
                            <Typography variant="body2" component="span">
                              {`${device.part_id} ${device.description ?? ''} — ${device.confirmed_qty}/${
                                device.expected_qty
                              }`}
                            </Typography>
                            {device.failed_inspection && (
                              <Chip size="small" color="error" label={text.failed} sx={{ marginLeft: '0.5rem' }} />
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(event.quality_record || event.customer_checklist) && (
                    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                      {event.quality_record && (
                        <Button onClick={() => openFile(event.quality_record as string)}>{text.qualityRecord}</Button>
                      )}
                      {event.customer_checklist && (
                        <Button onClick={() => openFile(event.customer_checklist as string)}>
                          {text.customerChecklist}
                        </Button>
                      )}
                    </div>
                  )}
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      )}
    </Page>
  );
};

export default SerialNumberHistory;
